import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createSubscription } from '../../api/subscriptions';
import { PushService } from '../../services/PushService';
import { Button, Field, Input, inlineLinkClass } from '../ui';
import { cn } from '../../utils/cn';

type Channel = 'telegram' | 'email' | 'push';

const BOT_NAME = process.env.REACT_APP_TELEGRAM_BOT_NAME || 'schnellerwohnen_bot';

const CHANNELS: Array<{ value: Channel; title: string; desc: string; needsEmail: boolean }> = [
  { value: 'telegram', title: 'Telegram', desc: '1 Klick, sofort startklar. Kein Passwort nötig.', needsEmail: false },
  { value: 'email', title: 'E-Mail', desc: 'Nur deine E-Mail-Adresse. Kein Passwort nötig.', needsEmail: true },
  { value: 'push', title: 'Browser-Push', desc: 'Sofort im Browser. Ideal als installierte App.', needsEmail: true },
];

/**
 * The real PLZ → channel entry point, embedded directly in the hero. Ported from
 * `origin/redesign/web-app-2026`'s `components/site/QuickStart.tsx`: `useAuth().registerMagic`,
 * `createSubscription` and `PushService` all match this branch's current signatures unchanged,
 * so only the markup was restyled onto this branch's `ui` primitives (the reference used a
 * `Label` component that doesn't exist on this branch).
 */
export interface QuickStartProps {
  /** Notifies the parent (LandingPage) which step is active, e.g. so its mobile sticky
   * CTA — which focuses the now-gone `#hero-plz` input — can hide itself on step 2 (M8). */
  onStepChange?: (step: 1 | 2) => void;
}

export const QuickStart: React.FC<QuickStartProps> = ({ onStepChange }) => {
  const { registerMagic } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2>(1);

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);
  const [plz, setPlz] = useState('');
  const [channel, setChannel] = useState<Channel>('telegram');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isValidPlz = useMemo(() => /^\d{5}$/.test(plz), [plz]);
  const needsEmail = CHANNELS.find((c) => c.value === channel)?.needsEmail ?? false;

  const goToStep2 = useCallback(() => {
    if (!isValidPlz) {
      setError('Bitte gib eine fünfstellige Postleitzahl ein.');
      return;
    }
    setError('');
    setStep(2);
  }, [isValidPlz]);

  const submit = useCallback(async () => {
    setError('');

    if (channel === 'telegram') {
      window.open(`https://t.me/${BOT_NAME}?start=plz_${plz}`, '_blank');
      return;
    }

    if (needsEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Bitte gib eine gültige E-Mail-Adresse an.');
      return;
    }

    setSubmitting(true);
    try {
      if (channel === 'push') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          setError('Push-Benachrichtigungen wurden nicht erlaubt. Bitte erlaube sie in den Browser-Einstellungen.');
          setSubmitting(false);
          return;
        }
        await registerMagic(email);
        await PushService.subscribeToPush();
        try {
          await PushService.sendTestNotification();
        } catch {
          /* non-critical */
        }
        await createSubscription({ zipCodes: [plz], notificationChannels: ['push'] });
      } else {
        await registerMagic(email);
        await createSubscription({ zipCodes: [plz], notificationChannels: ['email'] });
      }
      navigate('/dashboard');
    } catch (err: any) {
      setError(err?.message || 'Etwas ist schiefgelaufen. Bitte versuche es erneut.');
    } finally {
      setSubmitting(false);
    }
  }, [channel, email, needsEmail, plz, registerMagic, navigate]);

  if (step === 1) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          goToStep2();
        }}
        noValidate
      >
        <Field
          label="Wo suchst du?"
          htmlFor="hero-plz"
          hint="Kostenlos, ohne Zahlungsdaten. Filter stellst du danach ein."
          error={error || undefined}
        >
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              id="hero-plz"
              inputMode="numeric"
              pattern="\d{5}"
              maxLength={5}
              placeholder="Postleitzahl, z. B. 10115"
              value={plz}
              onChange={(e) => {
                setPlz(e.target.value.replace(/\D/g, ''));
                setError('');
              }}
              autoComplete="postal-code"
              invalid={!!error}
              aria-describedby="hero-plz-hint"
              className="sm:max-w-[220px]"
            />
            {/* Always enabled (a dimmed CTA reads as broken in dark mode); an invalid PLZ is
                reported on submit instead. Same h-11 as the Input so the row lines up. */}
            <Button type="submit" size="md" className="px-6">
              Suche starten
            </Button>
          </div>
        </Field>
      </form>
    );
  }

  return (
    <div>
      <button type="button" onClick={() => setStep(1)} className="mb-4 text-sm font-medium text-muted hover:text-ink">
        PLZ ändern
      </button>

      <p className="text-sm text-muted">
        PLZ <span className="tabular font-semibold text-ink">{plz}</span> — wie möchtest du benachrichtigt werden?
      </p>

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {CHANNELS.map((c) => {
          const active = channel === c.value;
          return (
            <button
              key={c.value}
              type="button"
              aria-pressed={active}
              onClick={() => {
                setChannel(c.value);
                setError('');
              }}
              className={cn(
                'flex flex-col items-start gap-1 rounded-control border p-3 text-left transition-colors',
                active ? 'border-brand bg-brand-soft' : 'border-line-strong bg-card hover:border-brand/50'
              )}
            >
              <span className="text-sm font-semibold text-ink">{c.title}</span>
              <span className="text-xs text-muted">{c.desc}</span>
            </button>
          );
        })}
      </div>

      {needsEmail && (
        <div className="mt-4">
          <Field label="E-Mail-Adresse" htmlFor="hero-email">
            <Input
              id="hero-email"
              type="email"
              placeholder="deine@email.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </Field>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-4 text-sm text-danger-text">
          {error}
        </p>
      )}

      <Button onClick={submit} size="lg" disabled={submitting} className="mt-5 w-full sm:w-auto">
        {submitting ? 'Wird eingerichtet…' : channel === 'telegram' ? 'Telegram öffnen' : 'Suche aktivieren'}
      </Button>

      <p className="mt-3 text-sm text-muted">
        Mit dem Start legst du ein kostenloses Konto an. Details in der{' '}
        <Link to="/datenschutz" className={inlineLinkClass}>
          Datenschutzerklärung
        </Link>
        .
      </p>
    </div>
  );
};

export default QuickStart;
